import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../redux/AlertController';

const API = `${process.env.REACT_APP_API_BASE_URL}/api/admin/points`;

// "نقاطي" management: program config + tasks, submissions review, balances, stats.
// `program` = 'user' | 'driver' (sent as ?program= on every call).
export const usePoints = (program = 'user') => {
    const user = useSelector((state) => state.userController.user);
    const dispatch = useDispatch();
    const [config, setConfig] = useState(null);
    const [defaults, setDefaults] = useState(null);

    const request = useCallback(async (path, { method = 'GET', body } = {}) => {
        const sep = path.includes('?') ? '&' : '?';
        const response = await fetch(`${API}${path}${sep}program=${program}`, {
            method,
            headers: {
                Authorization: `Bearer ${user?.token}`,
                ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            },
            ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(json.error || 'حدث خطأ غير متوقع');
        return json;
    }, [user?.token, program]);

    const notify = useCallback((msg, type = 'success') => dispatch(alertActions.showAlert({ msg, type })), [dispatch]);

    const loadConfig = useCallback(async () => {
        if (!user?.token) return;
        try {
            const json = await request('/config');
            setConfig(json.config);
            setDefaults(json.defaults);
        } catch (error) {
            notify(error.message, 'error');
        }
    }, [user?.token, request, notify]);

    useEffect(() => {
        setConfig(null);
        loadConfig();
    }, [loadConfig]);

    const saveConfig = async (payload) => {
        try {
            const json = await request('/config', { method: 'PUT', body: payload });
            setConfig(json.config);
            notify('تم حفظ إعدادات النقاط');
            return true;
        } catch (error) {
            notify(error.message, 'error');
            return false;
        }
    };

    // Read helpers return null on failure (after showing the error).
    const safe = async (promise) => {
        try {
            return await promise;
        } catch (error) {
            notify(error.message, 'error');
            return null;
        }
    };

    const listSubmissions = ({ status = 'pending', type = '', before = '' } = {}) => {
        const qs = new URLSearchParams({ status, ...(type ? { type } : {}), ...(before ? { before } : {}) });
        return safe(request(`/submissions?${qs}`));
    };
    const approveSubmission = async (id, points) => {
        const json = await safe(request(`/submissions/${id}/approve`, { method: 'POST', body: { points } }));
        if (json) notify(json.message);
        return Boolean(json);
    };
    const rejectSubmission = async (id, note) => {
        const json = await safe(request(`/submissions/${id}/reject`, { method: 'POST', body: { note } }));
        if (json) notify(json.message);
        return Boolean(json);
    };
    const searchUsers = (q) => safe(request(`/users/search?q=${encodeURIComponent(q)}`));
    const getUser = (id, before = '') => safe(request(`/users/${id}${before ? `?before=${encodeURIComponent(before)}` : ''}`));
    const adjustUser = async (id, points, reason) => {
        const json = await safe(request(`/users/${id}/adjust`, { method: 'POST', body: { points, reason } }));
        if (json) notify(json.message);
        return json;
    };
    const getStats = (from, to) => {
        const qs = new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) });
        return safe(request(`/stats?${qs}`));
    };

    return { config, defaults, loadConfig, saveConfig, listSubmissions, approveSubmission, rejectSubmission, searchUsers, getUser, adjustUser, getStats };
};
